import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme, type ThemePreference } from 'pi-kiosk-shared/theme';
import { forceAdminAppUpdateFromSettings } from '../../app/pwa/forceAdminAppUpdateFromSettings.js';
import { isAdminCriticalFlowActive } from '../../app/pwa/adminCriticalFlowGate.js';
import { isAdminPwaInstalled } from '../../app/pwa/adminInstallOfferCooldown.js';
import {
  hasPositiveAdminPwaInstallEvidence,
  markAdminPwaInstallEvidence,
  stampAdminPwaInstallEvidenceIfStandalone,
} from '../../app/pwa/adminPwaInstallEvidence.js';
import {
  peekAdminPwaInstallPrompt,
  subscribeAdminPwaInstallPrompt,
  takeAdminPwaInstallPrompt,
} from '../../app/pwa/adminPwaInstallPromptStore.js';
import { HeaderLanguagePills } from './HeaderLanguagePills.js';
import { DetailDrawer } from './DetailDrawer.js';

export interface AdminSettingsSheetProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Busy-dismiss gate (DetailDrawer isBusy) — Escape/backdrop blocked when true. */
  readonly isBusy?: boolean;
}

const THEME_OPTIONS: readonly {
  readonly value: Extract<ThemePreference, 'light' | 'dark'>;
  readonly labelKey: string;
}[] = [
  { value: 'light', labelKey: 'chrome.settings.themeLight' },
  { value: 'dark', labelKey: 'chrome.settings.themeDark' },
];

const GATE_POLL_MS = 400;

function readNavigatorOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/i.test(ua) && !/CriOS/i.test(ua);
}

/**
 * System settings overlay — Language, Appearance (Light/Dark), Install & Update.
 * Auth (Sign out / Sign in) lives on the profile icon via AdminProfileSheet.
 * Hosted on DetailDrawer variant=sheet (LOCK A) + DialogCloseButton Lucide X 20/44.
 */
export function AdminSettingsSheet({
  open,
  onClose,
  isBusy = false,
}: AdminSettingsSheetProps): JSX.Element {
  const { t } = useTranslation('admin');
  const { preference, effectiveTheme, setTheme } = useTheme();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [criticalFlowActive, setCriticalFlowActive] = useState(() =>
    isAdminCriticalFlowActive(),
  );
  const [navigatorOffline, setNavigatorOffline] = useState(() => readNavigatorOffline());
  const [offlineAttemptHint, setOfflineAttemptHint] = useState(false);
  const [runningAsInstalledApp, setRunningAsInstalledApp] = useState(() =>
    isAdminPwaInstalled(),
  );
  const [hasInstallPrompt, setHasInstallPrompt] = useState(
    () => peekAdminPwaInstallPrompt() !== null,
  );
  const [installHint, setInstallHint] = useState<
    'already' | 'ios' | 'unavailable' | null
  >(null);

  const selectedTheme: 'light' | 'dark' =
    preference === 'light' || preference === 'dark' ? preference : effectiveTheme;

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const syncGates = (): void => {
      setCriticalFlowActive(isAdminCriticalFlowActive());
      stampAdminPwaInstallEvidenceIfStandalone();
      setRunningAsInstalledApp(isAdminPwaInstalled());
      const offline = readNavigatorOffline();
      setNavigatorOffline(offline);
      if (!offline) {
        setOfflineAttemptHint(false);
      }
    };

    // queueMicrotask: avoid react-hooks/set-state-in-effect (cascading render)
    queueMicrotask(syncGates);
    const intervalId = window.setInterval(syncGates, GATE_POLL_MS);
    window.addEventListener('online', syncGates);
    window.addEventListener('offline', syncGates);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('online', syncGates);
      window.removeEventListener('offline', syncGates);
    };
  }, [open]);

  useEffect(() => {
    const syncPrompt = (): void => {
      setHasInstallPrompt(peekAdminPwaInstallPrompt() !== null);
    };
    syncPrompt();
    return subscribeAdminPwaInstallPrompt(syncPrompt);
  }, []);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onAppInstalled = (): void => {
      markAdminPwaInstallEvidence();
      setRunningAsInstalledApp(true);
      setInstallHint(null);
      takeAdminPwaInstallPrompt();
      setHasInstallPrompt(false);
    };
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, [open]);

  const sheetBusy = isBusy || isUpdating || isInstalling;
  const updateDisabled = isUpdating || criticalFlowActive;
  const showCriticalHint = criticalFlowActive;
  const showOfflineHint =
    !criticalFlowActive && (navigatorOffline || offlineAttemptHint);
  const showInstallButton = !runningAsInstalledApp;

  const handleUpdate = useCallback(async (): Promise<void> => {
    if (isUpdating || isAdminCriticalFlowActive()) {
      return;
    }
    setOfflineAttemptHint(false);
    setIsUpdating(true);
    try {
      const outcome = await forceAdminAppUpdateFromSettings();
      if (outcome === 'blocked_critical_flow') {
        setIsUpdating(false);
        return;
      }
      if (outcome === 'offline') {
        setOfflineAttemptHint(true);
        setIsUpdating(false);
        return;
      }
    } catch {
      setIsUpdating(false);
    }
  }, [isUpdating]);

  const handleInstall = useCallback(async (): Promise<void> => {
    if (isInstalling || isUpdating || isAdminCriticalFlowActive()) {
      return;
    }
    setInstallHint(null);

    // Positive evidence first (standalone, persisted flag, getInstalledRelatedApps).
    // Also covers iOS Safari browser-tab after a prior Home Screen / standalone open.
    if (await hasPositiveAdminPwaInstallEvidence()) {
      markAdminPwaInstallEvidence();
      setInstallHint('already');
      await handleUpdate();
      return;
    }

    // take() — exclusive ownership so Lifecycle banner cannot prompt() a spent BIP.
    const promptEvent = takeAdminPwaInstallPrompt();
    if (promptEvent !== null) {
      setHasInstallPrompt(false);
      setIsInstalling(true);
      try {
        await promptEvent.prompt();
        const choice = await promptEvent.userChoice;
        if (choice.outcome === 'accepted') {
          markAdminPwaInstallEvidence();
        }
      } catch {
        // Browser may reject prompt(); leave Install available if a new BIP arrives.
      } finally {
        setIsInstalling(false);
      }
      return;
    }

    if (isIosSafari()) {
      // Pure Safari tab, no install evidence → Share / A2HS guide only.
      setInstallHint('ios');
      return;
    }

    // No BIP and not positively installed — do not overclaim "already installed".
    setInstallHint('unavailable');
  }, [handleUpdate, isInstalling, isUpdating]);

  return (
    <DetailDrawer
      open={open}
      onClose={onClose}
      title={t('chrome.settings.title')}
      variant="sheet"
      placement="top"
      closeGlyph="x"
      isBusy={sheetBusy}
      contentTestId="admin-settings-sheet"
      overlayTestId="admin-settings-sheet-backdrop"
      closeTestId="admin-settings-sheet-close"
      exportExclude="chrome"
    >
      <div className="flex flex-col gap-4">
        <section className="flex flex-col gap-2" aria-labelledby="admin-settings-language-label">
          <h3
            id="admin-settings-language-label"
            className="text-sm font-medium text-[var(--color-an-text)]"
          >
            {t('chrome.settings.language')}
          </h3>
          <HeaderLanguagePills placement="header" dataTestId="admin-settings-language" />
        </section>

        <section className="flex flex-col gap-2" aria-labelledby="admin-settings-appearance-label">
          <h3
            id="admin-settings-appearance-label"
            className="text-sm font-medium text-[var(--color-an-text)]"
          >
            {t('chrome.settings.appearance')}
          </h3>
          <div
            className="flex flex-wrap gap-2"
            role="radiogroup"
            aria-labelledby="admin-settings-appearance-label"
            data-testid="admin-settings-appearance"
          >
            {THEME_OPTIONS.map((option) => {
              const selected = selectedTheme === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  data-testid={`admin-settings-theme-${option.value}`}
                  className={[
                    'inline-flex min-h-11 min-w-11 flex-1 items-center justify-center rounded-md border px-3 text-sm font-medium transition-colors',
                    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-an-focus-ring)]',
                    selected
                      ? 'border-[var(--color-an-primary)] bg-[var(--color-an-primary)] text-[var(--color-an-on-primary,#ffffff)]'
                      : 'border-[var(--color-an-border)] bg-[var(--color-an-surface)] text-[var(--color-an-text)] hover:bg-[var(--color-an-surface-hover)]',
                  ].join(' ')}
                  onClick={() => {
                    setTheme(option.value);
                  }}
                >
                  {t(option.labelKey)}
                </button>
              );
            })}
          </div>
        </section>

        <section
          className="flex flex-col gap-2"
          aria-labelledby="admin-settings-install-update-label"
        >
          <h3
            id="admin-settings-install-update-label"
            className="text-sm font-medium text-[var(--color-an-text)]"
          >
            {t('chrome.settings.installAndUpdate')}
          </h3>
          <div className="flex flex-wrap gap-2">
            {showInstallButton ? (
              <button
                type="button"
                disabled={isInstalling || isUpdating || criticalFlowActive}
                aria-busy={isInstalling || undefined}
                data-testid="admin-settings-install"
                data-has-prompt={hasInstallPrompt ? 'true' : 'false'}
                className={[
                  'inline-flex min-h-11 min-w-11 max-w-[50%] flex-1 items-center justify-center rounded-md border px-3 text-sm font-medium transition-colors',
                  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-an-focus-ring)]',
                  'border-[var(--color-an-border)] bg-[var(--color-an-surface)] text-[var(--color-an-text)]',
                  isInstalling || isUpdating || criticalFlowActive
                    ? 'cursor-not-allowed opacity-60'
                    : 'hover:bg-[var(--color-an-surface-hover)]',
                ].join(' ')}
                onClick={() => {
                  void handleInstall();
                }}
              >
                {isInstalling
                  ? t('chrome.settings.installBusy')
                  : t('chrome.settings.installAction')}
              </button>
            ) : null}
            <button
              type="button"
              disabled={updateDisabled}
              aria-busy={isUpdating || undefined}
              data-testid="admin-settings-update"
              className={[
                'inline-flex min-h-11 min-w-11 max-w-[50%] flex-1 items-center justify-center rounded-md border px-3 text-sm font-medium transition-colors',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-an-focus-ring)]',
                'border-[var(--color-an-border)] bg-[var(--color-an-surface)] text-[var(--color-an-text)]',
                updateDisabled
                  ? 'cursor-not-allowed opacity-60'
                  : 'hover:bg-[var(--color-an-surface-hover)]',
              ].join(' ')}
              onClick={() => {
                void handleUpdate();
              }}
            >
              {isUpdating ? t('chrome.settings.updateBusy') : t('chrome.settings.updateAction')}
            </button>
          </div>
          {installHint === 'already' ? (
            <p
              className="text-xs text-[var(--color-an-text-muted)]"
              data-testid="admin-settings-install-hint"
            >
              {t('chrome.settings.installAlreadyInstalled')}
            </p>
          ) : null}
          {installHint === 'ios' ? (
            <p
              className="text-xs text-[var(--color-an-text-muted)]"
              data-testid="admin-settings-install-hint"
            >
              {t('chrome.settings.installIosGuide')}
            </p>
          ) : null}
          {installHint === 'unavailable' ? (
            <p
              className="text-xs text-[var(--color-an-text-muted)]"
              data-testid="admin-settings-install-hint"
            >
              {t('chrome.settings.installUnavailable')}
            </p>
          ) : null}
          {showCriticalHint ? (
            <p
              className="text-xs text-[var(--color-an-text-muted)]"
              data-testid="admin-settings-update-hint"
            >
              {t('chrome.settings.updateDeferredCritical')}
            </p>
          ) : null}
          {showOfflineHint ? (
            <p
              className="text-xs text-[var(--color-an-text-muted)]"
              data-testid="admin-settings-update-hint"
            >
              {t('chrome.settings.updateOffline')}
            </p>
          ) : null}
        </section>
      </div>
    </DetailDrawer>
  );
}
